/**
 * Pure helpers behind the API Explorer's request forms. They live outside the components so the
 * payload transformations — which operate on a user-editable JSON body and therefore cannot trust
 * its shape — are unit-testable without rendering.
 */
import { GraphQL } from "@rpi/openapi-api";
import { isArray, isEmptyString, isRecord, isString } from "../../../utils/type.util";
import { PLACEHOLDER } from "./apiExplorerSpec";

type ApiCredential = GraphQL.ListApiCredentialsQuery["listApiCredentials"][number];
type CustomerProduct = GraphQL.ListCustomerProductsQuery["listCustomerProducts"][number];

/** The asset URLs the Explorer writes into, and reads back out of, each order item's product. */
export interface ProductFileUrls {
  coverUrl: string;
  gutsUrl: string;
}

/**
 * Credentials belong to one environment, and the Explorer sends real requests, so a session offers
 * only the keys for the mode it is in — a production session creates production orders, a sandbox
 * session sandbox ones.
 */
export function selectCredentialsForEnvironment(
  credentials: ApiCredential[] | undefined,
  isSandbox: boolean
): ApiCredential[] | undefined {
  return credentials?.filter((credential) => credential.isSandbox === isSandbox);
}

/** Products are offered in catalog order so the list is stable between renders. */
export function sortProductsForSelect(products?: CustomerProduct[]): CustomerProduct[] | undefined {
  return products?.slice().sort((a, b) => a.openapiProductId - b.openapiProductId);
}

/** The first order item, when the body still holds a list of them. */
function readFirstOrderItem(payload: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!isArray(payload.orderItems)) {
    return undefined;
  }
  const [firstItem] = payload.orderItems;
  return isRecord(firstItem) ? firstItem : undefined;
}

/**
 * Reads the asset URLs back out of the first order item, falling back to the placeholder tokens.
 * Every step is guarded: the body is user-editable, so any of it may be missing or the wrong type,
 * and a non-string URL would otherwise reach the field as its value.
 */
export function readProductFileUrls(payload: Record<string, unknown>): ProductFileUrls {
  const product = readFirstOrderItem(payload)?.product;
  const cover = isRecord(product) ? product.coverUrl : undefined;
  const guts = isRecord(product) ? product.gutsUrl : undefined;
  return {
    coverUrl: isString(cover) ? cover : PLACEHOLDER.coverUrl,
    gutsUrl: isString(guts) ? guts : PLACEHOLDER.gutsUrl,
  };
}

/**
 * Writes the selected product's asset URLs (and optionally its sku) into the first order item — the
 * same one the form's fields read from, so extra items a caller added are left as they typed them.
 *
 * Returns the payload untouched when the Data editor no longer holds an order item to write into.
 */
export function applyFileUrlsToFirstOrderItem(
  payload: Record<string, unknown>,
  fileUrls: Partial<ProductFileUrls>,
  sku?: string
): Record<string, unknown> {
  if (!isArray(payload.orderItems)) {
    return payload;
  }
  const [firstItem, ...rest] = payload.orderItems;
  if (!isRecord(firstItem)) {
    return payload;
  }
  const product = isRecord(firstItem.product) ? firstItem.product : {};
  return {
    ...payload,
    orderItems: [
      {
        ...firstItem,
        ...(sku === undefined ? {} : { sku }),
        product: { ...product, ...fileUrls },
      },
      ...rest,
    ],
  };
}

function isPlaceholderFileUrl(url: string): boolean {
  return url === PLACEHOLDER.coverUrl || url === PLACEHOLDER.gutsUrl;
}

/**
 * Narrows derived template URLs to the fields still holding a placeholder token, so a URL the caller
 * typed survives metadata resolving after they typed it — including on a cache hit, which fires again
 * for a product they have already picked.
 */
export function unsetFileUrls(
  payload: Record<string, unknown>,
  derived: Partial<ProductFileUrls>
): Partial<ProductFileUrls> {
  const current = readProductFileUrls(payload);
  return {
    ...(current.coverUrl === PLACEHOLDER.coverUrl && derived.coverUrl ? { coverUrl: derived.coverUrl } : {}),
    ...(current.gutsUrl === PLACEHOLDER.gutsUrl && derived.gutsUrl ? { gutsUrl: derived.gutsUrl } : {}),
  };
}

/**
 * The URL a file field should show, or an empty string when there is nothing to show.
 *
 * The Data body carries placeholder tokens until a product's template metadata resolves. A token is
 * not a URL, so it reads as an empty field showing its placeholder — the state the design draws —
 * rather than as text the caller has to clear before typing.
 */
export function editableFileUrl(url: string): string {
  return isPlaceholderFileUrl(url) ? "" : url;
}

/**
 * Whether a real product has been picked. The body carries the seed's `{sku}` token until one is, and
 * a token is not a product — so Submit stays disabled rather than posting a placeholder order.
 */
export function isSelectedSku(sku: string): boolean {
  return !isEmptyString(sku) && sku !== PLACEHOLDER.sku;
}

/** The asset URLs to seed when a product is picked but its template metadata has not loaded yet. */
export const UNRESOLVED_FILE_URLS: ProductFileUrls = {
  coverUrl: PLACEHOLDER.coverUrl,
  gutsUrl: PLACEHOLDER.gutsUrl,
};
